import {SlicePipe} from '@angular/common';
import {ChangeDetectionStrategy, Component, computed, effect, input, output, signal} from '@angular/core';
import {MatIcon} from '@angular/material/icon';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {RepeatingTaskHistoryItem} from 'src/app/core/model/project';
import {CurrentSkedTask} from 'src/app/core/model/task';
import {User} from 'src/app/core/model/user';
import {TaskStatuses} from 'src/app/current-sked/shared/task-statuses';
import {SharedCurrentSkedModule} from 'src/app/shared/current-sked/shared-current-sked.module';
import {OphAvatarMultiComponent} from 'src/app/shared/design/oph-avatar-multi/oph-avatar-multi.component';
import {OphCheckboxComponent} from 'src/app/shared/design/oph-checkbox/oph-checkbox.component';
import {OphIconModule} from 'src/app/shared/design/oph-icon/oph-icon.module';
import {OphPaginatorComponent} from 'src/app/shared/design/oph-paginator/oph-paginator.component';
import {IsTaskExpiredPipe} from 'src/app/shared/pipes/is-task-expired.pipe';
import {PipesModule} from 'src/app/shared/pipes/pipes.module';
import {getTaskStatusColors, getTaskStatusDisplay} from '../task-status.utils';
import {TaskStatusInfoPipe} from './task-status-info.pipe';

@Component({
  selector: 'project-view-task',
  standalone: true,
  imports: [
    PipesModule,
    OphIconModule,
    OphAvatarMultiComponent,
    MatIcon,
    SlicePipe,
    MatProgressSpinnerModule,
    OphPaginatorComponent,
    SharedCurrentSkedModule,
    TaskStatusInfoPipe,
    OphCheckboxComponent,
    IsTaskExpiredPipe,
  ],
  templateUrl: './project-view-task.component.html',
  styleUrl: './project-view-task.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectViewTaskComponent {
  task = input.required<CurrentSkedTask>();
  usersMap = input<Record<string, User>>({});
  type = input<string>();
  user = input<User>();
  isExpanded = input<boolean>();
  repeatingTaskHistory = input<RepeatingTaskHistoryItem>();

  taskClick = output<{task: CurrentSkedTask; isRepeating: boolean}>();
  pageChange = output<number>();
  checkboxClick = output<{checked: boolean; task: CurrentSkedTask}>();
  routineClick = output<string>();

  firstLoad = signal(true);

  loading = signal(false);

  taskStatuses = TaskStatuses;

  expandedState = computed(() => {
    const repeatingHistory = this.repeatingTaskHistory();

    if (repeatingHistory) {
      return repeatingHistory.expanded;
    }

    const fallbackExpanded = this.isExpanded() ?? false;
    return fallbackExpanded;
  });

  // Computed property for activity users to avoid running the pipe multiple times
  activityUsers = computed(() => {
    const activities = this.task()?.activity;
    if (!activities || !Array.isArray(activities)) {
      return [];
    }

    // Extract updatedBy user IDs and filter out duplicates
    const userIds = activities
      .map(activity => activity.updatedBy)
      // System is a possible ID for a user, used on the backend, we won't display it
      .filter((userId, index, arr) => userId && userId !== 'system' && arr.indexOf(userId) === index);

    return userIds;
  });

  constructor() {
    // Reset firstLoad after initial isExpanded state is set
    effect(() => {
      this.expandedState(); // Subscribe to changes
      if (this.firstLoad()) {
        // Use setTimeout to allow the initial state to render without animation
        setTimeout(() => this.firstLoad.set(false), 0);
      }
    });
  }

  onTask(task: CurrentSkedTask, isRepeating: boolean = false) {
    this.taskClick.emit({task, isRepeating});
  }

  onPageChange(page: number) {
    this.pageChange.emit(page);
  }

  onCheckbox(checked: boolean, task: CurrentSkedTask) {
    this.loading.set(true);
    this.checkboxClick.emit({checked, task});
  }

  onRoutine(taskId: string) {
    this.routineClick.emit(taskId);
  }

  getStatusDisplay(status: TaskStatuses): string {
    return getTaskStatusDisplay(status);
  }

  getStatusBackgroundColor(status: TaskStatuses): string {
    return getTaskStatusColors(status).backgroundColor;
  }

  getStatusBorderColor(status: TaskStatuses): string {
    return getTaskStatusColors(status).borderColor;
  }
}
